/**
 * Indicates whether the object has the specified property as its own property
 * (as opposed to inheriting it).
 *
 * > This is a wrapper around `Object.prototype.hasOwnProperty` so that
 * > `Object.prototype` built-ins are not called directly.  As recommended by
 * > [ESLint](https://eslint.org/docs/rules/no-prototype-builtins).
 *
 * @param theObject - The object to test.
 * @param theProperty - The String name or Symbol of the property to test.
 */
export function objectHasOwnProperty<T>(
    theObject: T,
    theProperty: PropertyKey
): theProperty is keyof T {
    return Object.prototype.hasOwnProperty.call(theObject, theProperty);
}

/**
 * Convert string value to boolean (case-insensitive)
 *
 * - True  values - yes,  true, t,  on, 1
 * - False values -  no, false, f, off, 0
 *
 * @param value - The string value to convert to boolean (e.g. 'yes', 'off',
 * 'false', etc.)
 * @param defaultValue - The default value if the string is unknown and cannot
 * be converted.
 */
export function stringToBoolean(value: string, defaultValue = false): boolean {
    switch (value.toLowerCase()) {
        case 'yes':
        case 'true':
        case 't':
        case 'on':
        case '1':
            return true;
        case 'no':
        case 'false':
        case 'f':
        case 'off':
        case '0':
            return false;
        default:
            return defaultValue;
    }
}

/**
 * Convert string (case-insensitive) or other values to boolean
 *
 * String values will be converted via the {@link stringToBoolean} function.
 *
 * The values `null` and `undefined` will return the value from the
 * `defaultValue` parameter.
 *
 * Values other than `string`, `null`, and `undefined` will be converted via the
 * `Boolean()` function.
 *
 * @param value - The value to convert to boolean (e.g. 'yes', 'off', 'false',
 * 0, 1, etc.)
 * @param defaultValue - The default value if the string is unknown and cannot
 * be converted or if the the value is `null/undefined`.
 */
export function toBoolean(value: unknown, defaultValue = false): boolean {
    switch (typeof value) {
        case 'boolean':
            return value;
        case 'string':
            return stringToBoolean(value, defaultValue);
        case 'undefined':
            return defaultValue;
        default:
            if (value === null) {
                return defaultValue;
            } else {
                return Boolean(value);
            }
    }
}

/**
 * @param value Any type other that `string` or `number` will return `NaN`
 * @returns
 */
export function toNumber(value: unknown): number {
    switch (typeof value) {
        case 'string':
            return ['0o', '0b', '0x'].includes(value.substring(0, 2).toLowerCase())
                ? Number(value)
                : parseFloat(value);
        case 'number':
            return value;
        default:
            return NaN;
    }
}

/**
 * @param value Any type other that `string` or `number` will return `NaN`
 * @returns
 */
export function toInt(value: unknown): number {
    const numValue = toNumber(value);
    if (Number.isNaN(numValue)) {
        return NaN;
    }
    return Math.floor(numValue);
}

/** The count ID for {@link uniqueId}. Can be 0-4999. */
let uniqueIdCount = 0;
/**
 * Returns a unique ID based off Time and a Counter.
 *
 * Note: Collision could occur if more than 5000 ID's are created in a
 * millisecond. I don't believe JavaScript is fast enough for this to be a
 * concern.
 * @param sep - The separator character between ID types.
 */
export function uniqueId(sep = '.'): string {
    uniqueIdCount = (uniqueIdCount + 1) % 5000;
    const timeId = Date.now().toString(36);
    const countId = uniqueIdCount.toString(36);
    return `${timeId}${sep}${countId}`;
}

/**
 * A workaround to prevent Chrome from displaying the scary non-error "Refused
 * to get unsafe header" in the console when accessing headers that don't exist
 * via `XMLHttpRequest.getResponseHeader`.
 * @see {@link https://trackjs.com/blog/refused-unsafe-header/}
 */
export function xhrGetResponseHeader(xhr: XMLHttpRequest, headerName: string): string | null {
    // https://trackjs.com/blog/refused-unsafe-header/
    // get rid of scary non-error "Refused to get unsafe header"
    if (xhr.getAllResponseHeaders().toLowerCase().indexOf(headerName.toLowerCase()) >= 0) {
        console.debug('found header:', headerName, xhr.getResponseHeader(headerName));
        return xhr.getResponseHeader(headerName);
    }
    return null;
}

/**
 * For use with environment variables (`process.env.*`).
 * @example
 * ```ts
 * envVar(process.env.BASE_URL_EMERGENCY_ALERTS) + '/configuration/by_id/' + configId
 * ```
 * @throws TypeError if an environment variable is not defined
 * @param environmentVariable
 * @returns
 */
export function envVar(environmentVariable: string | undefined): string {
    if (typeof environmentVariable === 'string') {
        return environmentVariable;
    }
    throw new TypeError('Environment variable not defined.');
}
